import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import SeparatorSelector from './SeparatorSelector'
import ThousandsSeparatorSelector from './ThousandsSeparatorSelector'
import DecimalsSeparatorSelector from './DecimalsSeparatorSelector'
import DateLocaleSelector from './DateLocaleSelector'
import StackSelector from './StackSelector'

import styles from './ParsingOptions.module.scss'
import { BsArrowRepeat } from 'react-icons/bs'
import { get } from 'lodash'
import { fetchData as fetchDataFromUrl } from '../DataLoader/loaders/UrlFetch'
import { fetchSparqlData } from '../DataLoader/loaders/sparqlFetchData'

const dataRefreshWorkers = {
  url: fetchDataFromUrl,
  sparql: fetchSparqlData,
}

const dataRefreshCaptions = {
  url: 'Refresh data from url',
  sparql: 'Refresh data from query',
}

const AUTO_REFRESH_INTERVALS = [
  { label: '1 min',  value: 1 },
  { label: '5 min',  value: 5 },
  { label: '10 min', value: 10 },
  { label: '30 min', value: 30 },
]

function useElapsedSeconds(anchorTime) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (!anchorTime) { setElapsed(0); return }
    const tick = () => setElapsed(Math.floor((Date.now() - anchorTime) / 1000))
    tick()
    const id = setInterval(tick, 5000)
    return () => clearInterval(id)
  }, [anchorTime])
  return elapsed
}

function formatElapsed(seconds) {
  if (seconds < 60) return 'just now'
  const mins = Math.floor(seconds / 60)
  return `${mins} min ago`
}

export default function ParsingOptions(props) {
  const dataSourceType = get(props.dataSource, 'type', '')
  const canRefresh = Boolean(dataRefreshWorkers[dataSourceType])
  const isUrlSource = dataSourceType === 'url'

  // Lifted state — controlled by parent (survives step navigation)
  const autoRefreshEnabled = props.autoRefreshEnabled ?? false
  const setAutoRefreshEnabled = props.setAutoRefreshEnabled ?? (() => {})
  const intervalMinutes = props.autoRefreshInterval ?? 10
  const setIntervalMinutes = props.setAutoRefreshInterval ?? (() => {})
  const lastRefreshedAt = props.lastRefreshedAt ?? null
  const setLastRefreshedAt = props.setLastRefreshedAt ?? (() => {})

  // Transient UI state — fine to reset on remount
  const [refreshing, setRefreshing] = useState(false)

  const elapsed = useElapsedSeconds(lastRefreshedAt)

  const refreshData = useCallback(async () => {
    const impl = dataRefreshWorkers[dataSourceType]
    if (!impl) return
    setRefreshing(true)
    try {
      const data = await impl(props.dataSource)
      props.onDataRefreshed(data)
      setLastRefreshedAt(Date.now())
    } finally {
      setRefreshing(false)
    }
  }, [dataSourceType, props.dataSource, props.onDataRefreshed, setLastRefreshedAt])

  // Auto-refresh interval
  const refreshRef = useRef(refreshData)
  useEffect(() => { refreshRef.current = refreshData }, [refreshData])

  useEffect(() => {
    if (!autoRefreshEnabled || !isUrlSource) return
    const ms = intervalMinutes * 60 * 1000
    const id = setInterval(() => refreshRef.current(), ms)
    return () => clearInterval(id)
  }, [autoRefreshEnabled, intervalMinutes, isUrlSource])

  return (
    <div className={styles.parsingOptions}>
      <div className={[styles.optionCell, styles.optionCellSeparator].join(' ')}>
        {props.userDataType === 'csv' && (
          <SeparatorSelector
            title="Column separator"
            value={props.separator}
            onChange={(nextSeparator) => props.setSeparator(nextSeparator)}
          />
        )}
      </div>

      <div className={[styles.optionCell, styles.optionCellThousands].join(' ')}>
        <ThousandsSeparatorSelector
          title="Thousands separator"
          value={props.thousandsSeparator}
          onChange={(nextSeparator) =>
            props.setThousandsSeparator(nextSeparator)
          }
        />
      </div>

      <div className={[styles.optionCell, styles.optionCellDecimals].join(' ')}>
        <DecimalsSeparatorSelector
          title="Decimals separator"
          value={props.decimalsSeparator}
          onChange={(nextSeparator) =>
            props.setDecimalsSeparator(nextSeparator)
          }
        />
      </div>

      <div className={[styles.optionCell, styles.optionCellLocale].join(' ')}>
        <DateLocaleSelector
          title="Date Locale"
          value={props.locale}
          onChange={(nextLocale) => props.setLocale(nextLocale)}
        />
      </div>

      <div className={[styles.optionCell, styles.optionCellStack].join(' ')}>
        <StackSelector
          title="Stack on"
          value={props.stackDimension}
          list={props.dimensions}
          onChange={(nextStackDimension) =>
            props.setStackDimension(nextStackDimension)
          }
        />
      </div>

      {canRefresh && (
        <div className={styles['refresh-row']}>
          <Button
            color="primary"
            className={styles['refresh-button']}
            onClick={refreshData}
            disabled={refreshing}
          >
            <BsArrowRepeat className={refreshing ? styles['spin'] : 'mr-2'} />
            {refreshing ? 'Refreshing…' : get(dataRefreshCaptions, dataSourceType, 'Refresh data')}
          </Button>

          {isUrlSource && (
            <label className={styles['auto-refresh-toggle']}>
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
              />
              <span>Auto-refresh every</span>
              <select
                className={styles['interval-select']}
                value={intervalMinutes}
                onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                disabled={!autoRefreshEnabled}
              >
                {AUTO_REFRESH_INTERVALS.map(({ label, value }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          )}

          {lastRefreshedAt && (
            <span className={styles['last-refreshed']}>
              Updated {formatElapsed(elapsed)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
